import React, { useCallback, useRef } from "react";

import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { Branch } from "Models/branch";
import { Company } from "Models/company";
import { Customer } from "Models/customer";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useActions } from "./hooks/useActions";
import {
  DisabledGridActionItemContainer,
  EmptyDiv,
  EmptyText,
  ExportCSVButton,
  GridActionContainer,
  GridActionItemContainer,
  LoadingSpinner,
  OpenSubscriptionDrawIconButton,
  RightActionsGrid,
} from "./styles";
import {
  DateSelectorFilter,
  PeriodOptions,
} from "../ManagementReportScreen/components/DateSelector";
import { capitalizeFirstCharacter } from "Util/Format";
import { DefaultDateOptionsColumn, DefaultPeriodOptionsColumn } from "Constants";
import { colors } from "@zapalacorp/tokens";
import {
  ArrowDown2,
  ArrowUp2,
  Calendar,
  DocumentDownload,
  ExportSquare as RedirectIcon,
  Sms,
} from "iconsax-react";
import InputDateRange from "Components/Form/Input/DateRange";
import { Filters } from "Models/shipeezi.table";
import { CostingReportCSV, OrdersManagement } from "Models/report.orders.management";
import { TasksGridAfterRow } from "./tasksGridAfterRow";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { Color } from "Components/Form/Button";
import { jsonToCsv } from "Util/generateCsv";
import { useAppState } from "Context/StateContext";
import { Cronjob } from "Models/cronjob";
import { CronjobFrequency, CronJobType, ExportReportType, ReportName } from "Util/enums";
import { useTheme } from "@mui/material/styles";
import {
  ManageEmailSubscriptionEditorDrawer,
  ManageEmailSubscriptionEditorDrawerHandles,
} from "Components/ManageEmailSubscriptionEditorDrawer";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { GenerateJob } from "Models/generate.job";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";

export interface DateOption {
  name: string;
  column: string;
}

const today = new Date();

export const CostingReportScreen = () => {
  const {
    branchesList,
    companies,
    defaultEndDate,
    defaultStartDate,
    endDate,
    filters,
    getOrdersManagements,
    handleSearch,
    openRows,
    orderId,
    ordersManagementsReports,
    page,
    pageSize,
    period,
    setEndDate,
    setFilters,
    setOpenRows,
    setOrderId,
    setPage,
    setPageSize,
    setStartDate,
    setPeriod,
    startDate,
    total,
    useCreateEmailSubscription,
    useExportOrderCSVData,
    useExportSingleOrderCSVData,
    useGeneratePDFView,
    useGetJobStatus,
    useGetJobType,
  } = useActions();

  const { isFetching: loadingOrdersManagements } = getOrdersManagements();
  const { data: jobStatus } = useGetJobStatus();
  const { data: jobTypes } = useGetJobType();

  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const theme = useTheme();

  const manageEmailSubscriptionEditorDrawerRef =
    useRef<ManageEmailSubscriptionEditorDrawerHandles>(null);

  const getLastDaysLabel = (days: number) => {
    return `${capitalizeFirstCharacter(translate("last"))} ${days} ${translate(
      "days"
    ).toLocaleLowerCase()}`;
  };

  const periodOptions: PeriodOptions[] = [
    {
      name: getLastDaysLabel(7),
      column: DefaultPeriodOptionsColumn.LAST_WEEK,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 7,
    },
    {
      name: getLastDaysLabel(30),
      column: DefaultPeriodOptionsColumn.LAST_MONTH,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 30,
    },
    {
      name: getLastDaysLabel(90),
      column: DefaultPeriodOptionsColumn.LAST_QUARTER,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 90,
    },
    {
      name: getLastDaysLabel(365),
      column: DefaultPeriodOptionsColumn.LAST_YEAR,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 365,
    },
    {
      name: translate("lifetime"),
      column: DefaultPeriodOptionsColumn.LIFETIME,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      description: translate("reportLifetimeData"),
    },
    {
      name: translate("dateRange"),
      column: DefaultPeriodOptionsColumn.CUSTOM_PERIOD,
      type: DefaultDateOptionsColumn.DATE_RANGE,
      icon: <Calendar size={12} color={colors.gray800} />,
    },
  ];

  const setStartEndDates = (period: string, startDate: Date, endDate: Date) => {
    switch (period) {
      case DefaultPeriodOptionsColumn.LAST_WEEK:
        setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_MONTH:
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_QUARTER:
        setStartDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_YEAR:
        setStartDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LIFETIME:
        setStartDate(new Date(Date.now() - 20 * 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_DATE:
        setStartDate(startDate || "");
        setEndDate(startDate || "");
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        setStartDate(startDate || "");
        setEndDate(endDate || "");
        break;
      default:
        setStartDate(undefined);
        setEndDate(undefined);
    }
  };

  const onChangePeriod = (period: DefaultPeriodOptionsColumn) => {
    setStartEndDates(period, startDate ?? defaultStartDate, endDate ?? defaultEndDate);
    setPeriod(period);
    setPage(0);
  };

  const renderSelectPeriodFilters = () => {
    return <DateSelectorFilter options={periodOptions} value={period} onChange={onChangePeriod} />;
  };

  const renderInputDateRange = () => {
    switch (period) {
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        const getStartDate = () => {
          if (startDate === undefined) return new Date(today.getFullYear(), today.getMonth(), 1);
          if (startDate) return new Date(startDate);

          return undefined;
        };

        const getEndDate = () => {
          if (endDate === undefined) return today;
          if (endDate) return new Date(endDate);

          return undefined;
        };

        return (
          <InputDateRange
            pStartDate={getStartDate()}
            pEndDate={getEndDate()}
            onChange={(range) => {
              setStartDate(range?.startDate);
              setEndDate(range?.endDate);
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleGenerateOrdersCSV = (data: CostingReportCSV[]) => {
    const formatedDate = format(
      utcToZonedTime(today, Intl.DateTimeFormat().resolvedOptions().timeZone),
      "dd_MM_yyyy"
    );

    const filename = `${formatedDate}_Costing_report_${currentUser?.company.name
      ?.split(" ")
      .join("_")}`;
    const extension = "csv";
    const fileNameWithExtension = `${filename}.${extension}`;
    const jsonString = `data:text/csv;chatset=utf-8,${encodeURIComponent(jsonToCsv(data))}`;
    const link = document.createElement("a");

    link.href = jsonString;
    link.download = fileNameWithExtension;
    document.body.appendChild(link);

    link.click();
    link.remove();
  };

  const { mutate: exportOrdersData, isLoading: isExportingCSV } =
    useExportOrderCSVData(handleGenerateOrdersCSV);

  const { mutate: exportSingleOrderData, isLoading: isExportingSingleCSV } =
    useExportSingleOrderCSVData(handleGenerateOrdersCSV);

  const renderDownloadCSVButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
      >
        <DefaultTooltip title={translate("exportCSV")} placement="top">
          <div>
            <ExportCSVButton
              loading={isExportingCSV}
              customColor={Color.SECONDARY}
              disabled={isExportingCSV}
              onClick={() => exportOrdersData()}
              startIcon={<DocumentDownload size={20} color={colors.white} />}
            >
              {translate("exportCSV")}
            </ExportCSVButton>
          </div>
        </DefaultTooltip>
      </RightActionsGrid>
    );
  };

  const createSubscriptionSuccess = () => {
    NotificationToast.success(translate("operationPerformedSuccessfully"));
    manageEmailSubscriptionEditorDrawerRef?.current?.close();
  };

  const createSubscriptionError = (errorMessage?: string) => {
    NotificationToast.error(errorMessage);
  };

  const { mutate: createSubscription, isLoading: isCreatingCronJob } = useCreateEmailSubscription(
    createSubscriptionSuccess,
    createSubscriptionError
  );

  const renderHandleSubscriptionButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
      >
        <DefaultTooltip title={translate("sendReportByEmail")}>
          <div>
            <OpenSubscriptionDrawIconButton
              onClick={() =>
                manageEmailSubscriptionEditorDrawerRef?.current?.open({
                  filters: JSON.stringify({ ...filters, startDate, endDate }),
                  frequency: CronjobFrequency.ONCE,
                } as Cronjob)
              }
            >
              <Sms size={21} color={theme.palette.primary.main} />
            </OpenSubscriptionDrawIconButton>
          </div>
        </DefaultTooltip>
      </RightActionsGrid>
    );
  };

  const formatDate = (date: string) => {
    if (date) {
      return format(
        utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
        "dd/MM/yyyy hh:mm a"
      );
    }

    return "-";
  };

  const columns: Column[] = [
    {
      name: "company",
      title: translate("company"),
      formatText: (company: Company) => company?.name,
      hideSearch: true,
    },
    {
      name: "branchFrom",
      hideSearch: true,
      title: translate("originBranch"),
      formatComponent: (branchFrom: Branch) => branchFrom?.name || "-",
    },
    {
      name: "toBranch",
      hideSearch: true,
      title: translate("destinationBranch"),
      formatComponent: (branchTo: Branch) => branchTo?.name || "-",
    },
    {
      name: "type",
      hideSearch: true,
      title: translate("type"),
    },
    {
      name: "reference",
      title: translate("reference"),
      formatComponent: (reference: string, job: OrdersManagement) =>
        `${reference}${job.sequence ? `/${job.sequence}` : ""}`,
    },
    {
      name: "customer",
      title: translate("customer"),
      formatText: (customer: Customer) => customer?.fullName,
    },
    {
      name: "tasks",
      hideSearch: true,
      title: translate("tasks"),
      formatText: (tasks: Task[]) => tasks?.length ?? 0,
    },
    {
      name: "createdAt",
      title: translate("orderCreated"),
      hideSearch: true,
      formatComponent: formatDate,
    },
    {
      name: "updatedAt",
      title: translate("lastUpdated"),
      hideSearch: true,
      formatComponent: formatDate,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeJobStatus value={status} />,
      hideSearch: true,
    },
    {
      name: "id",
      title: translate("actions"),
      hideSearch: true,
      formatComponent: (_, order: OrdersManagement) => {
        return renderRowActions(order);
      },
    },
  ];

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <EmptyText>{translate("noRecordFound")}</EmptyText>
      </EmptyDiv>
    );
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const getJobTypes = () => {
    const jobTypesExtracted: string[] = [];

    if (jobTypes && jobTypes.length > 0) {
      jobTypes?.forEach((jobType) => {
        if (jobType && jobType?.name) {
          jobTypesExtracted.push(jobType?.name);
        }
      });
    }

    return jobTypesExtracted;
  };

  const getStatuses = () => {
    const statusesExtracted: string[] = [];

    if (jobStatus && jobStatus.length > 0) {
      jobStatus?.forEach((status) => {
        if (status && status?.name) {
          statusesExtracted.push(status?.name);
        }
      });
    }

    const uniqueStatuses = Array.from(new Set(statusesExtracted));
    return uniqueStatuses;
  };

  const toggleCollapse = useCallback((orderId?: number) => {
    if (orderId) {
      setOpenRows((prev) => {
        if (prev.find((item) => item === orderId)) {
          return prev.filter((item) => item !== orderId);
        }

        return [...prev, orderId];
      });
    }
  }, []);

  const renderShowTasksButton = (order: OrdersManagement) => {
    const orderId = order?.id;

    if (!order?.tasks?.length || !orderId) return null;

    const isOpen = !!openRows.find((item) => item === orderId);

    return (
      <DefaultTooltip title={translate(isOpen ? "collapse" : "expand")}>
        <GridActionItemContainer active={isOpen}>
          {isOpen ? (
            <ArrowUp2 color={colors.gray600} size={16} onClick={() => toggleCollapse(orderId)} />
          ) : (
            <ArrowDown2 color={colors.gray600} size={16} onClick={() => toggleCollapse(orderId)} />
          )}
        </GridActionItemContainer>
      </DefaultTooltip>
    );
  };

  const { mutate: generateCostingPDF, isLoading: isGeneratingCostingPDF } = useGeneratePDFView();

  const renderViewOrderPDFButton = (order: OrdersManagement) => {
    if (!order?.tasks?.length) return null;

    if (isGeneratingCostingPDF && order?.id === orderId) {
      return (
        <DisabledGridActionItemContainer>
          <LoadingSpinner size={16} />
        </DisabledGridActionItemContainer>
      );
    }

    if (isGeneratingCostingPDF || isExportingSingleCSV) {
      return (
        <DisabledGridActionItemContainer>
          <RedirectIcon color={colors.gray400} size={16} />
        </DisabledGridActionItemContainer>
      );
    }

    const generateJobReport = {
      jobs: [order.id],
      type: ExportReportType.PDF,
    } as GenerateJob;

    return (
      <DefaultTooltip title={translate("view")}>
        <GridActionItemContainer>
          <RedirectIcon
            color={colors.gray600}
            size={16}
            onClick={() => {
              setOrderId(order.id);
              generateCostingPDF(generateJobReport);
            }}
          />
        </GridActionItemContainer>
      </DefaultTooltip>
    );
  };

  const renderExportSingleCSVButton = (order: OrdersManagement) => {
    if (!order?.tasks?.length || !order?.tasks?.some((task) => !!task.expenses?.length))
      return null;

    if (isExportingSingleCSV && order?.id === orderId) {
      return (
        <DisabledGridActionItemContainer>
          <LoadingSpinner size={16} />
        </DisabledGridActionItemContainer>
      );
    }

    if (isExportingSingleCSV || isGeneratingCostingPDF) {
      return (
        <DisabledGridActionItemContainer>
          <DocumentDownload color={colors.gray400} size={16} />
        </DisabledGridActionItemContainer>
      );
    }

    return (
      <DefaultTooltip title={translate("exportCSV")}>
        <GridActionItemContainer>
          <DocumentDownload
            color={colors.gray600}
            size={16}
            onClick={() => {
              setOrderId(order.id);
              exportSingleOrderData();
            }}
          />
        </GridActionItemContainer>
      </DefaultTooltip>
    );
  };

  const renderRowActions = (order: OrdersManagement) => {
    return (
      <GridActionContainer>
        {renderExportSingleCSVButton(order)}
        {renderViewOrderPDFButton(order)}
        {renderShowTasksButton(order)}
      </GridActionContainer>
    );
  };

  const renderAfterRow = (orderManagement: OrdersManagement) => {
    const isOpen = !!(orderManagement?.id && openRows?.includes(orderManagement?.id));

    if (!isOpen) return null;

    return (
      <TasksGridAfterRow
        tasks={orderManagement.tasks.map((task) => ({
          ...task,
          branchFrom: orderManagement?.branchFrom,
        }))}
        loading={false}
      />
    );
  };

  return (
    <>
      <ShipeeziNewGrid
        loadingData={loadingOrdersManagements}
        emptyData={() => renderEmptyDiv()}
        data={ordersManagementsReports}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        columns={columns}
        totalItems={total}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        showColumnChooser={false}
        showActionLabel={false}
        positionActionsColumn="right"
        showSelection={false}
        renderAfterRow={renderAfterRow}
        generalActions={{
          orderBy: {
            right: ["handleSubscriptions", "exportCSV"],
            left: ["handleSelectPeriod", "dateSelector", "handleSearch", "handleFilterBy"],
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("type"),
                column: "type",
                options: getJobTypes(),
              },
              {
                name: translate("company"),
                column: "companyId",
                options: companies?.map((company) => {
                  return {
                    label: company?.name ?? "",
                    value: company?.id ? String(company?.id) : "",
                  };
                }),
                optionsSelected: filters["companyId"] as string[],
              },
              {
                name: translate("order"),
                column: "hasTasks",
                options: [
                  { label: translate("haveTask"), value: "1" },
                  { label: translate("dontHaveTask"), value: "0" },
                ],
              },
              {
                name: translate("originBranch"),
                column: "branchFromId",
                options: branchesList?.map((branch: Branch) => {
                  return {
                    label: branch?.name ?? "",
                    value: branch?.id ? String(branch?.id) : "",
                  };
                }),
              },
              {
                name: translate("destinationBranch"),
                column: "toBranchId",
                options: branchesList?.map((branch: Branch) => {
                  return {
                    label: branch?.name ?? "",
                    value: branch?.id ? String(branch?.id) : "",
                  };
                }),
              },
              {
                name: translate("status"),
                column: "status",
                options: getStatuses(),
              },
            ],
            onChange: handleFilter,
          },
          handleSearch: {
            onSearch: handleSearch,
          },
          handleSubscriptions: renderHandleSubscriptionButton(),
          exportCSV: renderDownloadCSVButton(),
          handleSelectPeriod: renderSelectPeriodFilters(),
          dateSelector: renderInputDateRange(),
        }}
      />

      <ManageEmailSubscriptionEditorDrawer
        ref={manageEmailSubscriptionEditorDrawerRef}
        isLoading={isCreatingCronJob}
        submitCronJob={(cronJob) =>
          cronJob &&
          createSubscription({
            ...cronJob,
            type: CronJobType.ORDER_AND_TASK_REPORT,
            companyId: currentUser?.companyId,
            name: ReportName.COSTING_REPORT,
          })
        }
      />
    </>
  );
};
